import React, { useContext, useEffect, useState } from "react";
import { Stack } from "@mui/material";
import { AdapterDayjs } from "components/V2/atoms/CustomDatePickerV2/AdapterDayjs";
import { DatePicker, DatePickerToolbar } from "components/V2/atoms/CustomDatePickerV2/DatePicker";
import { LocalizationProvider } from "components/V2/atoms/CustomDatePickerV2/LocalizationProvider";
import { PickersDay } from "components/V2/atoms/CustomDatePickerV2/PickersDay";
import { pickersDayClasses } from "components/V2/atoms/CustomDatePickerV2/PickersDay/pickersDayClasses";
import { LowFareIcon } from "components/icons/LowFareIcon";
import enLocale from "date-fns/locale/en-NZ";
import dayjs from "dayjs";
import moment from "moment";
import { TextFieldV2 } from "components/V2/atoms/TextFieldV2/TextFieldV2";
import { DATE_AND_TIME_FORMATS } from "../../../../constants/variables";
import { CommonDataContext } from "../../../../context/CommonDataContext";
import { getLowFareForMonth } from "../../../../dataLayer/lowFares";
import { currencyConverter } from "../../../../helper/currencyConverter";
import { extractResource } from "../../../../helper/extractResource";
import { useRouter } from "next/router";

interface PricingCalendarV2Props {
  selectedOptionOrg: string;
  selectedOptionDest: string;
  label: string;
  selectedDate: string;
  minDate?: string;
  onChange: (date: Date) => void;
  selectedCurrency: string;
}
type LowFareAPIResponseData = {
  date: string;
  isLowest: boolean;
  noFlights: boolean;
  price: number;
  soldOut: boolean;
};

export function PricingCalendarV2({
  selectedOptionOrg,
  selectedOptionDest,
  label,
  selectedDate,
  minDate,
  onChange,
  selectedCurrency
}: PricingCalendarV2Props) {
  const presentDate: string = dayjs().format();

  const { resources, selectedCurrencyDetails } = useContext(CommonDataContext);

  const resourceData = resources;
  const router = useRouter();
  const onlyHomePage = router.asPath === "/";

  const [getLowFareData, setLowFareData] = useState<LowFareAPIResponseData[]>();

  useEffect(() => {
    let apiCallMade = false;

    const getLowFarePrices = async (presentDateAsParameter: Date | string) => {
      try {
        const apiResponse: any = await getLowFareForMonth(
          selectedOptionOrg,
          selectedOptionDest,
          presentDateAsParameter,
          selectedCurrency
        );
        if (apiResponse?.data) {
          setLowFareData(apiResponse.data.data);
        } else {
          setLowFareData([]);
        }
      } catch (err) {
        console.warn("Error in getLowFarePrices :", err);
      }
    };

    if (
      selectedOptionOrg &&
      selectedOptionOrg !== "" &&
      selectedOptionDest &&
      selectedOptionDest !== "" &&
      selectedOptionOrg !== selectedOptionDest &&
      !apiCallMade
    ) {
      const startDate = minDate || presentDate;
      getLowFarePrices(startDate);
      apiCallMade = true;
    }
  }, [selectedOptionDest, selectedOptionOrg, selectedCurrency, minDate, presentDate]);

  // eslint-disable-next-line react/no-unstable-nested-components
  function CustomDay({ day, ...dayComponentProps }) {
    let lowPrice: any;
    let soldOut: any;
    let flightAvailable: boolean;

    if (getLowFareData && getLowFareData.length > 0) {
      const fareData = getLowFareData.find((elem: LowFareAPIResponseData) => {
        const tDate = moment(elem.date).format("YYYY/MM/DD").includes(moment(day.$d).format("YYYY/MM/DD"));
        return tDate;
      });

      if (!fareData)
        return (
          <PickersDay
            day={day}
            outsideCurrentMonth={false}
            isFirstVisibleCell={false}
            isLastVisibleCell={false}
            style={{
              position: "relative",
              fontSize: "0.8rem",
              width: "40px !important",
              height: "100% !important",
              borderRadius: "4px",
              margin: "0 2px"
            }}
            sx={{
              [`&&.${pickersDayClasses.selected}`]: {
                backgroundColor: "var(--color-bg-primary)",
                borderRadius: "4px 0px 4px 4px !important",
                color: "var(--color-primary)",
                border: "1px solid var(--color-primary)"
              }
            }}
            disabled
            {...dayComponentProps}
          />
        );
      const convertedLowPrice = currencyConverter(fareData?.price, selectedCurrency, selectedCurrencyDetails, true);
      lowPrice = !dayComponentProps.outsideCurrentMonth && convertedLowPrice;
      soldOut = !dayComponentProps.outsideCurrentMonth && fareData?.soldOut;
      flightAvailable = fareData.noFlights;

      return (
        <React.Fragment key={Number(day)}>
          <div className="relative">
            <PickersDay
              day={day}
              outsideCurrentMonth={false}
              isFirstVisibleCell={false}
              isLastVisibleCell={false}
              {...dayComponentProps}
              disabled={flightAvailable}
              style={{
                position: "relative",
                fontSize: "0.8rem",
                width: "40px !important",
                height: "40px !important",
                border: "none",
                borderRadius: "4px",
                color: fareData?.isLowest ? "var(--color-text-highlight-tertiary)" : "var(--color-text-secondary)",
                background: fareData?.isLowest ? "var(--color-bg-highlight-green)" : "var(--color-bg-tertiary)",
                margin: "0 2px"
              }}
              sx={{
                [`&&.${pickersDayClasses.selected}`]: {
                  backgroundColor: "var(--color-bg-primary) !important",
                  borderRadius: "4px 0px 4px 4px !important",
                  color: "var(--color-primary) !important",
                  border: "1px solid var(--color-primary) !important",
                  width: "36px",
                  height: "100%",
                  margin: "0px"
                },
                [`&&.${pickersDayClasses.today}`]: {
                  borderRadius: "0",
                  border: "none"
                }
              }}
            >
              {day.$D}
              <div className="pricing absolute bottom-0 m-[-1px] text-gray-700">
                <span
                  style={{
                    fontSize: "8px",
                    letterSpacing: "-1"
                  }}
                  className="pricing text-typography-secondary"
                >
                  {fareData.soldOut ? (
                    <p className="whitespace-nowrap" style={{ pointerEvents: "none" }}>
                      {soldOut && extractResource(resourceData, "soldOut")}
                    </p>
                  ) : (
                    <p>{lowPrice || undefined}</p>
                  )}
                </span>
              </div>
            </PickersDay>
          </div>
        </React.Fragment>
      );
    }

      return (
        <React.Fragment key={Number(day)}>
          <div className="relative">
            <PickersDay
              day={day}
              outsideCurrentMonth={false}
              isFirstVisibleCell={false}
              isLastVisibleCell={false}
              {...dayComponentProps}
              style={{
                position: "relative",
                fontSize: "0.85rem",
                width: "40px",
                height: "40px",
                borderRadius: "0px",
                margin: "0px"
              }}
              sx={{
                [`&&.${pickersDayClasses.selected}`]: {
                  backgroundColor: "var(--color-bg-default) !important",
                  borderRadius: "4px 0px 4px 4px !important",
                  color: "var(--color-primary) !important",
                  border: "1px solid var(--color-primary) !important",
                  width: "36px !important",
                  height: "100% !important"
                },
                [`&&.${pickersDayClasses.today}`]: {
                  borderRadius: "0",
                  border: "none"
                }
              }}
            />

          <div className="pricing absolute bottom-0 left-2 text-xs text-opacity-70" />
        </div>
      </React.Fragment>
    );
  }

  return (
    <>
      <LocalizationProvider locale={enLocale} dateAdapter={AdapterDayjs}>
        <DatePicker
          format={DATE_AND_TIME_FORMATS.DAY_DATE_MONTH_YEAR}
          label={label}
          sx={{
            "& .MuiFormLabel-root.MuiInputLabel-root": {
              maxWidth:"100%",
              overflow:"visible"
            }
          }}
          value={selectedDate ? dayjs(selectedDate) : null}
          onChange={(newValue: any) => {
            onChange(newValue.$d);
          }}
          minDate={minDate ? dayjs(minDate) : presentDate}
          renderInput={(params: any) => (
            <TextFieldV2
              {...params}
              InputLabelProps={{
                shrink: true
              }}
              fullWidth
            />
          )}
          slots={{
            day: CustomDay,
            toolbar: (props) => (
              <>
                <DatePickerToolbar {...props} />
                {onlyHomePage && (
                <div className="flex absolute top-0 right-0 bg-[#037E0B1A] text-[#037E0B] dark:text-gray-800 dark:bg-gray-50 p-1 px-2 font-normal text-sm items-center gap-2 z-10 rounded-b-md rounded-ee-none">
                  <LowFareIcon fill="inherit" width={18} height={18} />
                  <span>{extractResource(resourceData, "lowestFare")}</span>
                </div>
              )}
              </>
            )
          }}
          slotProps={{
            textField: { fullWidth: true },
            previousIconButton: {
              sx: {
                "&.Mui-disabled": {
                  color: "#D3D3D3"
                },
                "&:not(.Mui-disabled)": {
                  color: "var(--color-primary) !important"
                }
              }
            },
            nextIconButton: {
              sx: {
                color: "var(--color-primary) !important"
              }
            },
            actionBar: {
              sx: {
                "& .MuiButton-root:first-of-type": {
                  border: "1px solid var(--color-text-secondary) !important",
                  fontSize: "12px",
                  marginBottom: "10px",
                  marginRight: "2px",
                  color: (theme) => (theme.palette.mode === "dark" ? "white" : "#333333")
                },
                "& .MuiButton-root:last-of-type": {
                  backgroundColor: "var(--color-primary) !important",
                  fontSize: "12px",
                  marginRight: "22px",
                  marginBottom: "10px",
                  color: (theme) => (theme.palette.mode === "dark" ? "black" : "white")
                }
              }
            }
          }}
        />
      </LocalizationProvider>
      <style>
        {`

.pricing {
  display: flex;
  font-weight: 500;
  font-size: 9px;
  min-width: 20px;
  z-index: 1;
  transition: transform 225ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
  transform-origin: 100% 0%;
}

    `}
      </style>
    </>
  );
}
